// Enhanced rate limiting utility for Mailgun API
// This module provides centralized rate limiting, request throttling, and queue management

import { AxiosRequestConfig, AxiosResponse } from 'axios';

/**
 * Rate limiting configuration interface
 */
export interface RateLimitConfig {
  /** Maximum requests per minute (default: 75 to stay well under 4500/min limit) */
  requestsPerMinute: number;
  /** Maximum concurrent requests (default: 5) */
  maxConcurrent: number;
  /** Maximum retry attempts for rate limited requests (default: 3) */
  maxRetries: number;
  /** Base delay in milliseconds for exponential backoff (default: 1000) */
  baseDelay: number;
  /** Enable detailed logging (default: false) */
  enableLogging: boolean;
}

/**
 * Request queue item interface
 */
interface QueuedRequest {
  id: string;
  requestFn: () => Promise<any>;
  resolve: (value: any) => void;
  reject: (error: any) => void;
  retryCount: number;
  timestamp: number;
}

/**
 * Rate limiting statistics interface
 */
export interface RateLimitStats {
  totalRequests: number;
  successfulRequests: number;
  rateLimitedRequests: number;
  failedRequests: number;
  averageResponseTime: number;
  queueSize: number;
  activeRequests: number;
}

/**
 * Enhanced rate limiting manager for Mailgun API
 */
export class MailgunRateLimiter {
  private config: RateLimitConfig;
  private requestQueue: QueuedRequest[] = [];
  private activeRequests: Set<string> = new Set();
  private requestTimes: number[] = [];
  private stats: RateLimitStats = {
    totalRequests: 0,
    successfulRequests: 0,
    rateLimitedRequests: 0,
    failedRequests: 0,
    averageResponseTime: 0,
    queueSize: 0,
    activeRequests: 0
  };
  private processingQueue = false;

  constructor(config: Partial<RateLimitConfig> = {}) {
    this.config = {
      requestsPerMinute: 75, // Conservative limit to stay well under 4500/min
      maxConcurrent: 5,
      maxRetries: 3,
      baseDelay: 1000,
      enableLogging: false,
      ...config
    };

    // Start queue processing
    this.startQueueProcessor();
  }

  /**
   * Execute a request with rate limiting
   */
  async executeRequest<T>(
    requestFn: () => Promise<T>,
    requestId?: string
  ): Promise<T> {
    const id = requestId || `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    return new Promise<T>((resolve, reject) => {
      const queuedRequest: QueuedRequest = {
        id,
        requestFn,
        resolve,
        reject,
        retryCount: 0,
        timestamp: Date.now()
      };

      this.requestQueue.push(queuedRequest);
      this.stats.queueSize = this.requestQueue.length;
      
      if (this.config.enableLogging) {
        console.log(`[RateLimiter] Queued request ${id}. Queue size: ${this.requestQueue.length}`);
      }
    });
  }

  /**
   * Start the queue processor
   */
  private startQueueProcessor(): void {
    if (this.processingQueue) return;
    
    this.processingQueue = true;
    this.processQueue();
  }

  /**
   * Process the request queue with rate limiting
   */
  private async processQueue(): Promise<void> {
    while (this.processingQueue) {
      // Check if we can process more requests
      if (this.requestQueue.length === 0) {
        await this.sleep(100); // Short sleep when queue is empty
        continue;
      }

      // Check concurrency limit
      if (this.activeRequests.size >= this.config.maxConcurrent) {
        await this.sleep(100);
        continue;
      }

      // Check rate limit (requests per minute)
      if (!this.canMakeRequest()) {
        const waitTime = this.getWaitTimeForNextRequest();
        if (this.config.enableLogging) {
          console.log(`[RateLimiter] Rate limit reached. Waiting ${waitTime}ms`);
        }
        await this.sleep(waitTime);
        continue;
      }

      // Process next request
      const request = this.requestQueue.shift();
      if (request) {
        this.stats.queueSize = this.requestQueue.length;
        this.processRequest(request);
      }
    }
  }

  /**
   * Process a single request
   */
  private async processRequest(request: QueuedRequest): Promise<void> {
    this.activeRequests.add(request.id);
    this.stats.activeRequests = this.activeRequests.size;
    this.stats.totalRequests++;

    const startTime = Date.now();

    try {
      if (this.config.enableLogging) {
        console.log(`[RateLimiter] Processing request ${request.id} (attempt ${request.retryCount + 1})`);
      }

      const result = await request.requestFn();
      
      // Record successful request
      this.recordRequestTime(startTime);
      this.stats.successfulRequests++;
      
      if (this.config.enableLogging) {
        console.log(`[RateLimiter] Request ${request.id} completed successfully`);
      }

      request.resolve(result);
    } catch (error: any) {
      if (this.config.enableLogging) {
        console.log(`[RateLimiter] Request ${request.id} failed:`, error.message);
      }

      // Handle rate limiting (429 errors)
      if (error.response?.status === 429) {
        this.stats.rateLimitedRequests++;
        
        if (request.retryCount < this.config.maxRetries) {
          // Retry with exponential backoff
          const delay = this.calculateBackoffDelay(request.retryCount);
          
          if (this.config.enableLogging) {
            console.log(`[RateLimiter] Rate limited. Retrying request ${request.id} in ${delay}ms (attempt ${request.retryCount + 1}/${this.config.maxRetries})`);
          }

          // Re-queue the request after delay
          setTimeout(() => {
            request.retryCount++;
            this.requestQueue.unshift(request); // Add to front of queue for priority
            this.stats.queueSize = this.requestQueue.length;
          }, delay);
        } else {
          this.stats.failedRequests++;
          request.reject(new Error(`Request ${request.id} failed after ${this.config.maxRetries} retries due to rate limiting`));
        }
      } else {
        // Non-rate-limit error
        this.stats.failedRequests++;
        request.reject(error);
      }
    } finally {
      this.activeRequests.delete(request.id);
      this.stats.activeRequests = this.activeRequests.size;
    }
  }

  /**
   * Check if we can make a request based on rate limits
   */
  private canMakeRequest(): boolean {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;
    
    // Remove old request times
    this.requestTimes = this.requestTimes.filter(time => time > oneMinuteAgo);
    
    return this.requestTimes.length < this.config.requestsPerMinute;
  }

  /**
   * Get wait time until next request can be made
   */
  private getWaitTimeForNextRequest(): number {
    if (this.requestTimes.length === 0) return 0;
    
    const oldestRequest = Math.min(...this.requestTimes);
    const waitTime = oldestRequest + 60000 - Date.now();
    
    return Math.max(waitTime, 100); // Minimum 100ms wait
  }

  /**
   * Record a request time for rate limiting calculations
   */
  private recordRequestTime(startTime: number): void {
    const now = Date.now();
    this.requestTimes.push(now);
    
    // Update average response time
    const responseTime = now - startTime;
    this.stats.averageResponseTime = 
      (this.stats.averageResponseTime * (this.stats.successfulRequests - 1) + responseTime) / 
      this.stats.successfulRequests;
  }

  /**
   * Calculate exponential backoff delay
   */
  private calculateBackoffDelay(retryCount: number): number {
    const jitter = Math.random() * 0.1; // Add 10% jitter
    return Math.floor(this.config.baseDelay * Math.pow(2, retryCount) * (1 + jitter));
  }

  /**
   * Sleep utility
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get current rate limiting statistics
   */
  getStats(): RateLimitStats {
    return { ...this.stats };
  }

  /**
   * Reset statistics
   */
  resetStats(): void {
    this.stats = {
      totalRequests: 0,
      successfulRequests: 0,
      rateLimitedRequests: 0,
      failedRequests: 0,
      averageResponseTime: 0,
      queueSize: this.requestQueue.length,
      activeRequests: this.activeRequests.size
    };
  }

  /**
   * Stop the queue processor
   */
  stop(): void {
    this.processingQueue = false;
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<RateLimitConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}

// Global rate limiter instance
let globalRateLimiter: MailgunRateLimiter | null = null;

/**
 * Get or create the global rate limiter instance
 */
export const getMailgunRateLimiter = (config?: Partial<RateLimitConfig>): MailgunRateLimiter => {
  if (!globalRateLimiter) {
    globalRateLimiter = new MailgunRateLimiter(config);
  } else if (config) {
    globalRateLimiter.updateConfig(config);
  }
  return globalRateLimiter;
};

/**
 * Convenience function to execute a rate-limited request
 */
export const rateLimitedRequest = async <T>(
  requestFn: () => Promise<T>,
  requestId?: string,
  config?: Partial<RateLimitConfig>
): Promise<T> => {
  const rateLimiter = getMailgunRateLimiter(config);
  return rateLimiter.executeRequest(requestFn, requestId);
};

/**
 * Convenience function for bulk operations with concurrency control
 */
export const rateLimitedBulkRequests = async <T>(
  requestFns: (() => Promise<T>)[],
  config?: Partial<RateLimitConfig>
): Promise<(T | Error)[]> => {
  const rateLimiter = getMailgunRateLimiter(config);
  
  const promises = requestFns.map((requestFn, index) => 
    rateLimiter.executeRequest(requestFn, `bulk_${index}`)
      .catch(error => error) // Convert rejections to Error objects
  );

  return Promise.all(promises);
};